/* eslint-disable */
// Importing common helper methods
import _ from 'lodash';
import { isLuhnNumber, NUMERIC } from './helperMethods';
import { REQUIRED_MSG } from './constants/commonValues';

const {
    zero,
    one,
    three,
    four,
    ten,
    forty,
    hundred,
    six,
    fourteen,
    nineteen,
} = NUMERIC;

const hexCodeRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const validators = {
    /**
     * Method used with two different cases. First if we use this method with redux form then it passes the different props and if we use this method with custom methods then it passes the different props.
     *
     * @param {String} value - Value of an input element
     * @param {String | Object} name - If we are using this method with redux form it pass an object which is all values of redux form and if we are using this method with custom method it will pass a string which is name of an input field
     * @param {String | Object} type - If we are using this method with redux form it pass an object which is all props of redux form and if we are using this method with custom method we are passing type of an input element which is input or select i.e: String.
     * @param {String} fieldName - Passing the name of Redux Form Field element
     * @param {String} customInputName - Passing the custom name for Redux Form Field element
     * @returns {String | Undefined} - Return the error message which is String or an undefined
     */
    REQUIRED: (value, name = '', type = 'input', customInputName = '') => {
        const val = value || typeof value === 'number';
        if (typeof type === 'object') {
            return val ? false : true;
        }
        if (typeof type === 'Array') {
            return val.length > 0 ? false : true;
        }
        return val
            ? undefined
            : type === 'input'
            ? `Please Enter ${name}`
            : `Please Select ${name}`;
    },

    REQUIRE: value => {
        return _.isEmpty(value) ? true : false;
    },

    EMAIL: value => {
        if (value && !/^[A-Za-z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/.test(value)) {
            return 'Invalid email address';
        } else if (value && value.length > 64) {
            return 'Email cannot be more than 64 characters';
        } else {
            return undefined;
        }
    },
    
    PHONE_NUMBER: value =>
        value && !/^[6-9]\d{9}$/i.test(value)
            ? 'Invalid phone number, must be 10 digits'
            : undefined,

    MAX_LENGTH: (value, maxLength) =>
        value && value.length > maxLength
            ? `Must be ${maxLength} characters or less`
            : undefined,

    MIN_LENGTH: (value, minLength) =>
        value && value.length < minLength
            ? `Must be ${minLength} characters or more`
            : undefined,

    MIN_MAX_LENGTH: (value, minLength, maxLength) =>
        value && value.length >= minLength && value.length <= maxLength
            ? undefined
            : `Must be ${minLength} to ${maxLength} characters long`,

    EXACT_LENGTH: (value, exactLength) =>
        value && value.length !== exactLength
            ? `Must be ${exactLength} characters`
            : undefined,

    GREATER_THAN_EQUAL_TO: (value, num) =>
        value && value < num
            ? `Must be greater than or equal to ${num}`
            : undefined,

    LESS_THAN_EQUAL_TO: num => value =>
        value && value > num
            ? `Must be less than or equal to ${num}`
            : undefined,

    NUMBER_IN_BETWEEN: (value, lowerLimit, upperLimit) =>
        value && value >= lowerLimit && value <= upperLimit
            ? undefined
            : `Must be between ${lowerLimit} to ${upperLimit}`,
    POSITIVE_NUMBER: value =>
        value && value > 0 ? undefined : `Must be greater than 0`,

    NUMERIC: value =>
        value && isNaN(Number(value)) ? 'Must be a number' : undefined,

    ALPHA: value =>
        value && /^[a-zA-Z ]+$/.test(value)
            ? undefined
            : 'Only alphabets are allowed',

    ALPHA_OR_EMPTY: value =>
        !value || /^[a-zA-Z ]+$/.test(value)
            ? undefined
            : 'Only alphabets are allowed',

    ALPHA_NUMERIC: value =>
        value && /[^a-zA-Z0-9 ]/i.test(value)
            ? 'Only alphanumeric characters'
            : undefined,

    ATLEAST_ONE_NUMBER: value =>
        value && /\d{1}/i.test(value)
            ? undefined
            : 'At least one number should be entered',

    ATLEAST_ONE_ALPHABET: value =>
        value && /[A-Z]{1}/i.test(value)
            ? undefined
            : 'At least one alphabet should be entered',

    PHONE_NUMBER_OR_EMAIL: value =>
        value && validators.PHONE_NUMBER(value) && validators.EMAIL(value)
            ? 'Enter a valid email or phone number'
            : undefined,

    CURRENT_PASSWORD: value =>
        value &&
        (validators.MIN_LENGTH(value, six) ||
            validators.MAX_LENGTH(value, forty))
            ? 'Password should be between 6 and 40 characters'
            : undefined,

    NEW_PASSWORD: value =>
        value &&
        (validators.MIN_LENGTH(value, six) ||
            validators.MAX_LENGTH(value, forty) ||
            validators.ATLEAST_ONE_ALPHABET(value) ||
            validators.ATLEAST_ONE_NUMBER(value))
            ? 'Password should be atleast 6 characters long, with one alphabet and one number'
            : undefined,

    EXPIRY_DATE: value => {
        const splitValues = _.split(value, '/');
        const month = splitValues[zero];
        const year = splitValues[one];
        const currrentDate = new Date();
        const currentMonth = currrentDate.getMonth();
        const currentYearInYY = currrentDate.getFullYear() % hundred;
        const monthList = [
            '01',
            '02',
            '03',
            '04',
            '05',
            '06',
            '07',
            '08',
            '09',
            '10',
            '11',
            '12',
        ];

        if (value) {
            return monthList.indexOf(month) > -one &&
                ((parseInt(year, ten) === currentYearInYY &&
                    parseInt(month, ten) >= currentMonth + one) ||
                    parseInt(year, ten) > currentYearInYY)
                ? undefined
                : 'Invalid expiry date';
        }
    },

    CVV: value =>
        value && validators.MIN_MAX_LENGTH(value, three, four)
            ? 'Invalid CVV'
            : undefined,

    CREDIT_CARD_NUMBER: (value, isSeparatorIncluded, separatorSymbol) => {
        if (value && isSeparatorIncluded) {
            value = _.split(value, separatorSymbol).join('');
        }
        const isCardLengthValid = validators.MIN_MAX_LENGTH(
            value,
            fourteen,
            nineteen
        )
            ? false
            : true;
        return value && isCardLengthValid && isLuhnNumber(parseInt(value, ten))
            ? undefined
            : 'Invalid card number';
    },

    IS_CREDIT_CARD_NUMBER_BLOCKED: (value, blockedBinPrefixesList) =>
        value && !_.includes(blockedBinPrefixesList, parseInt(value, ten))
            ? undefined
            : 'Invalid card number',

    INTEGER: value =>
        value && /^[0-9]+$/i.test(value) ? undefined : 'Must be an integer',

    URL: value =>
        value &&
        /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(
            value
        )
            ? undefined
            : 'Must be an valid url',

    IS_VALID_HEX_CODE: value =>
        !value || hexCodeRegex.test(value)
            ? undefined
            : 'Please Enter Valid Hex Code',
    IS_VALID_IFSC:(value)=>{
        const ifscValidate = ifscRegex.test(value.toUpperCase())
        if(!ifscValidate){
            return `Please enter a valid IFSC code`
        }  
        return undefined 
    }
};

export const checkNumberList = values => {
    const { PHONE_NUMBER } = validators;
    const data = values && JSON.parse(values);
    if (data && data.length) {
        const formatValues = data.some(item => PHONE_NUMBER(item.value));
        return formatValues;
    }
    return false;
};

export const checkEmailList = values => {
    const { EMAIL } = validators;
    const data = values && JSON.parse(values);
    if (data && data.length) {
        const formatValues = data.some(item => EMAIL(item.value));
        return formatValues;
    }
    return false;
};

export const VALIDATOR_ERROR_MESSAGES = {
    empty: '',
    required: (label) => `Please enter ${label} `,
    panLen: "Please enter 10 digit PAN Number. ",
    numbericType: "Only Numeric Values are accepted. ",
    aadhaarDigits: "",
    pinCodeLen: "Pin code must be exactly 6 digits long. ",
    emailValid: "Please enter valid email Id. ",
    mobileLen: "lease enter valid 10 digit mobile number. ",
    panValid: "Please enter valid PAN. ",
    gstValid: "Please enter valid gst number. ",
    aadhaarValid: "Please enter valid aadhaar number. ",
}

export const AADHAR_PATTERN = /^[0-9]{12}$/;
export const GST_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
export const PAN_PATTERN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

function validateData(data, regex) {
    return regex.test(data);
}

export const panValidation = (fieldData) => {
    let errors = VALIDATOR_ERROR_MESSAGES.empty;
    if (!validateData(fieldData, PAN_PATTERN)) {
        errors += VALIDATOR_ERROR_MESSAGES.panValid;
    }
    
    return !errors;
}

export const gstValidation = (fieldData) => {
    let errors = VALIDATOR_ERROR_MESSAGES.empty;
    
    if (!validateData(fieldData, GST_PATTERN)) {
        errors += VALIDATOR_ERROR_MESSAGES.gstValid;
    }
    
    return !errors;
}

export const aadhaarValidation = (fieldData) => {
    let errors = VALIDATOR_ERROR_MESSAGES.empty;
    
    if (!validateData(fieldData, AADHAR_PATTERN)) {
        errors += VALIDATOR_ERROR_MESSAGES.aadhaarValid;
    }
    
    return !errors;
}

const maxLength = max => value =>
    value && value.length > max
        ? `Must be ${max} characters or less`
        : undefined;

const minLength = min => value =>
    value && value.length <= min
        ? `Must be ${min} characters or more`
        : undefined;

const minValue = min => value =>
    value && value < min ? `Must be at least ${min}` : undefined;
export const fieldLevelValidators = {
    required: value => (value ? undefined : REQUIRED_MSG),
    number: value =>
        value && isNaN(Number(value)) ? 'Must be a number. ' : undefined,
    numeric: value =>
        value && isNaN(Number(value)) ? 'Must be Numeric. ' : undefined,
    maxLength30: maxLength(30),
    maxLength10: maxLength(10),
    minLength5: minLength(5),
    minLength10: minLength(10),
    minValue0: minValue(0),
    length10: value =>
        value && value.length !== 10 ? 'Length Must be equal to 10. ' : undefined,
    mobileNumber: value =>
        value && !/^[5-9]\d{9}$/.test(value)
            ? 'Invalid Mobile Number. '
            : undefined,

    containsNumber: value => 
        value && !(/\d/.test(value))
            ? 'Invalid no numeric value found'
            : undefined,
    alphanumeric: value =>
        value && !/^[a-zA-Z0-9]+$/.test(value)
            ? 'Invalid Characters. '
            : undefined,
    alpha: value =>
        value && !/^[a-zA-Z]+$/.test(value)
            ? 'Invalid Characters. '
            : undefined,
    alphaWithSpace : value =>
        value && !/^[a-zA-Z ]+$/.test(value)
            ? 'Invalid Characters. '
            : undefined,
    alphanumericWithSpace: value =>
        value && !/^[a-zA-Z0-9 ]+$/.test(value)
            ? 'Invalid Characters. '
            : undefined,
    capitalNumericWithoutSpace: value =>
        value && !/^[A-Z0-9]+$/.test(value) ? 'Invalid Characters. ' : undefined,
    email: value =>
        value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value)
            ? 'Invalid email address. '
            : undefined,
    pan: val =>
        val && panValidation(val) 
        ? undefined
        : VALIDATOR_ERROR_MESSAGES.panValid,
    gst: val =>
        val && gstValidation(val) 
        ? undefined
        : VALIDATOR_ERROR_MESSAGES.gstValid,
    aadhar: val =>
        val && aadhaarValidation(val) 
        ? undefined
        : VALIDATOR_ERROR_MESSAGES.aadhaarValid,
    pin: val =>
        val && val?.toString()?.length === 6
        ? undefined
        : VALIDATOR_ERROR_MESSAGES.pinCodeLen

};
